import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Container, Header, Tab } from 'semantic-ui-react';
import TaskExecutionsCreateModal from '../../../components/admin/task/taskExecutionsCreateModal';
import TaskExecutionsTable from '../../../components/admin/task/taskExecutionsTable';
import TaskExecutionUpdateModal from '../../../components/admin/task/taskExecutionUpdateModal';
import TaskForm from '../../../components/admin/task/taskForm';
import { getActionOption } from '../../../components/admin/task/utils';
import ConfirmationModal from '../../../components/confirmationModal';
import Loader from '../../../components/loader';
import { RESPONSE_STATUS } from '../../../constnats/general';
import { TASK_EXECUTION_TYPE, TASK_STATUS } from '../../../constnats/task';
import { ROLES } from '../../../constnats/user';
import useTask from '../../../hooks/task/useTask';
import useTaskExecutions from '../../../hooks/task/useTaskExecutions';
import useCompanies from '../../../hooks/useCompanies';
import useFullModalState from '../../../hooks/useFullModalState';
import useModalState from '../../../hooks/useModalState';
import useUsers from '../../../hooks/useUsers';
import {
    ExecutionConfigType,
    TaskExecutionInfoType,
    TaskExecutionsCreationSchemaType,
    TaskExecutionsSetSchemaType,
    TaskExecutionUpdateSchemaType,
    TaskUpdateSchemaType,
} from '../../../schemas/task.schemas';
import AuthService from '../../../service/auth';
import Alert from '../../../utils/alert';
import TasksRepo from '../../../utils/repository/tasksRepo/tasksRepo';
import { getPaneCfg } from '../../../utils/general';

function Task() {
    const params = useParams();
    const taskId = Number(params.taskId);
    const { task, loading: loadingTask, refetch: refetchTask } = useTask(taskId);
    const { users, loading: loadingUsers } = useUsers();
    const {
        executions,
        loading: loadingExecutions,
        refetch: refetchExecutions,
        page,
        setPage,
        totalPages,
    } = useTaskExecutions(taskId);
    const taskExecutionsCreateModalState = useModalState();
    const taskExecutionUpdateModalState = useFullModalState<TaskExecutionInfoType>();
    const createForAllModelState = useModalState();
    const deleteTaskExecutionModalState = useFullModalState<number>();
    const { companies, loading: loadingCompanies } = useCompanies();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [showLoading, setShowLoading] = useState(false)

    const handleTaskUpdate = (data: TaskUpdateSchemaType) => {
        const taskData: TaskUpdateSchemaType = {
            assignedUserId: data.assignedUserId || null,
            description: data.description,
            dueDate: data.dueDate,
            priority: data.priority,
            status: data.status,
            taskId: data.taskId,
            title: data.title,
        };
        TasksRepo.updateTask(data.taskId, taskData, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Task Updated Successfully!');
                refetchTask();
            } else {
                Alert.warn(response.message);
            }
        });
    };

    const handleEditTaskExecution = (taskExecution: TaskExecutionInfoType) => {
        taskExecutionUpdateModalState.openModal(taskExecution);
    };

    const handleUpdateTaskExecution = (execution: TaskExecutionUpdateSchemaType) => {
        TasksRepo.updateTaskExecution(execution.taskId, execution, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                taskExecutionUpdateModalState.closeModal();
                refetchExecutions();
                refetchTask();
            } else {
                Alert.warn(response.status);
            }
        });
    };

    const deleteTaskExecution = (execution: TaskExecutionInfoType) => {
        deleteTaskExecutionModalState.openModal(execution.id);
    };

    const handleDeleteTaskExecution = (executionId: number) => {
        TasksRepo.deleteTaskExecution(taskId, executionId, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Task Execution Deleted Successfully!');
                deleteTaskExecutionModalState.closeModal();
                refetchExecutions();
                refetchTask();
            } else {
                Alert.warn(response.message);
            }
        });
    };

    const addTaskExecutions = (data: TaskExecutionsCreationSchemaType) => {
        setShowLoading(true)
        TasksRepo.addTaskExecutions(data.taskId, data, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                taskExecutionsCreateModalState.closeModal();
                Alert.success('Task Executions have been added successfully!');
                refetchExecutions();
            } else {
                Alert.warn(response.message);
            }
            setShowLoading(false)
        });
    };

    const handleSetTaskExecutions = () => {
        setShowLoading(true)
        const taskExecutions: TaskExecutionsSetSchemaType = {
            taskId: taskId,
            executionConfigs: companies.map((c) => ({
                entityId: c.id,
                entityType: TASK_EXECUTION_TYPE.COMPANY,
            })),
        };

        TasksRepo.setTaskExecutions(taskExecutions.taskId, taskExecutions.executionConfigs, (response) => {
            if (response.status === RESPONSE_STATUS.SUCCESS) {
                Alert.success('Task Executions have been set successfully!');
                setPage(response.data.pageable.pageNumber);
                if (response.data.pageable.pageNumber === page) {
                    refetchExecutions();
                }
                refetchTask();
            } else {
                Alert.warn(response.message);
            }

            setShowLoading(false)
        });
        createForAllModelState.closeModal();
    };

    if (loadingTask || loadingUsers || loadingCompanies) {
        return <Loader />;
    }

    if (!task) {
        return <div>Task not found</div>;
    }

    //@ts-ignore
    const isTaskManager = AuthService.hasAnyGlobalRole([ROLES.TASKS_MANAGER_ROLE]);
    const isTaskCreator = AuthService.getUserId() === task.createdByUserId;
    const isTaskCompleted = task.status === TASK_STATUS.COMPLETED;
    const readOnly = isTaskCompleted || (!isTaskManager && !isTaskCreator);

    const executionsActions = isTaskManager ? [getActionOption('delete', 'Delete', deleteTaskExecution)] : [];

    const panes = [
        getPaneCfg('Task', <TaskForm task={task} users={users} onSave={handleTaskUpdate} readOnly={readOnly} />),
        getPaneCfg(
            'Executions',
            (loadingExecutions || showLoading) ? (
                <Loader />
            ) : (
                <TaskExecutionsTable
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    executions={executions}
                    showActions={true}
                    actions={executionsActions}
                    openExecution={handleEditTaskExecution}
                    additionalActions={
                        isTaskManager
                            ? [
                                  <Button
                                      key={'create-for'}
                                      className="ml-auto"
                                      onClick={taskExecutionsCreateModalState.openModal}
                                  >
                                      Create For
                                  </Button>,
                                  <Button key={'create-for-all'} onClick={createForAllModelState.openModal}>
                                      Create For All
                                  </Button>,
                              ]
                            : []
                    }
                />
            )
        ),
    ];

    return (
        <Container>
            <Header textAlign="center" as="h2">
                Task: {task.id}
            </Header>
            <Tab
                panes={panes}
                activeIndex={activeTabIndex}
                onTabChange={(_, data) => setActiveTabIndex(Number(data.activeIndex))}
            />
            <TaskExecutionsCreateModal
                taskId={task.id}
                open={taskExecutionsCreateModalState.open}
                companies={companies}
                onSave={addTaskExecutions}
                onCancel={() => taskExecutionsCreateModalState.closeModal()}
            />

            <TaskExecutionUpdateModal
                companies={companies}
                execution={taskExecutionUpdateModalState.modalData}
                open={taskExecutionUpdateModalState.open}
                onCancel={taskExecutionUpdateModalState.closeModal}
                onSave={handleUpdateTaskExecution}
            />

            {createForAllModelState.open && (
                <ConfirmationModal
                    onConfirm={handleSetTaskExecutions}
                    onCancel={createForAllModelState.closeModal}
                    msg="You are about to create an execution for EVERY Company. Are you sure?"
                />
            )}

            {deleteTaskExecutionModalState.modalData && (
                <ConfirmationModal
                    msg="Are you sure you want to delete the execution?"
                    onCancel={deleteTaskExecutionModalState.closeModal}
                    open={deleteTaskExecutionModalState.open}
                    onConfirm={() => handleDeleteTaskExecution(deleteTaskExecutionModalState.modalData!)}
                />
            )}
        </Container>
    );
}

export default Task;
